import { useMutation, useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from '@/constants';
import { handleResponse, get, post, put, del, patch } from './base.service';
import { handleError as appHandleError } from '@/utils/errorHandler';
import type {
  ApiResponse,
  CreateHotelPayload,
  UpdateHotelPayload,
  UpdateHotelDataPayload,
  UpdateHotelCommissionPayload
} from './types';
import {
  Hotel,
  HotelCommissionResponse,
  PaginatedResponse
} from '@/constants/Interfaces/CommonInterfaces';

export const useGetHotelCommissionQuery = (payload: string) =>
  useQuery<HotelCommissionResponse>({
    queryFn: async () => {
      const response = await get<HotelCommissionResponse>(
        `${API_ENDPOINTS.HOTEL_COMMISSION_RATES}?${payload}`
      );
      return handleResponse(response);
    },
    queryKey: ['hotel-commission', payload],
    refetchOnWindowFocus: false
  });

export const useUpdateHotelCommissionMutation = () =>
  useMutation<ApiResponse, Error, UpdateHotelCommissionPayload>({
    mutationFn: (payload: UpdateHotelCommissionPayload) => {
      return post(
        `${API_ENDPOINTS.HOTEL_COMMISSION_RATES}`,
        payload as Record<string, unknown>
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useCreateNewHotelMutation = () =>
  useMutation<ApiResponse, Error, CreateHotelPayload>({
    mutationFn: (payload: CreateHotelPayload) => {
      return post(
        `${API_ENDPOINTS.HOTELS}`,
        payload as Record<string, unknown>
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetHotelListQuery = (payload: string) =>
  useQuery<PaginatedResponse<Hotel>>({
    queryFn: async () => {
      const response = await get<PaginatedResponse<Hotel>>(
        `${API_ENDPOINTS.HOTELS}?${payload}`
      );
      return handleResponse(response);
    },
    queryKey: ['hotel-list', payload],
    refetchOnWindowFocus: false
  });

export const useUpdateHotelMutation = () =>
  useMutation<ApiResponse, Error, UpdateHotelDataPayload>({
    mutationFn: (payload: UpdateHotelDataPayload) => {
      return post(`${API_ENDPOINTS.HOTELS}/${payload.id}`, payload.data);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useDeleteHotelMutation = () =>
  useMutation<ApiResponse, Error, number>({
    mutationFn: (id: number) => {
      return del(`${API_ENDPOINTS.HOTELS}/${id}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetHotelDetailsQuery = (id: number) =>
  useQuery<Hotel>({
    queryFn: async () => {
      const response = await get<Hotel>(`${API_ENDPOINTS.HOTELS}/${id}`);
      return handleResponse(response);
    },
    queryKey: ['hotel-details', id],
    enabled: !!id,
    refetchOnWindowFocus: false
  });

export const useEditHotelMutation = () =>
  useMutation<ApiResponse, Error, UpdateHotelPayload>({
    mutationFn: (payload: UpdateHotelPayload) => {
      return put(`${API_ENDPOINTS.HOTELS}/${payload.id}`, payload.payload);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useUpdateHotelStatusMutation = () =>
  useMutation<ApiResponse, Error, number>({
    mutationFn: (id: number) => {
      return patch(`${API_ENDPOINTS.HOTELS}/${id}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });
